import { inject, Injectable } from '@angular/core';
import { BehaviorSubject, Observable, firstValueFrom } from 'rxjs';
import { UserDataShared as UserData } from './../models/interface/userData.interface';
import { CookieService } from 'ngx-cookie-service';
import { PromiseFactory, UtilsService } from './utils.service';
import { StorageService } from './storage.service';
import { StorageClassifier } from '../models/interface/storageClassifier.enum';
import { CacheService } from './cache.service';
import { environment } from 'src/environments/environment';
import { Router } from '@angular/router';

@Injectable({
  providedIn: 'root',
})
export class UserDataService {
  userData: BehaviorSubject<UserData>;
  userDataValue$: Observable<UserData>;
  tokenLocal: BehaviorSubject<string>;
  tokenLocalValue$: Observable<string>;
  userDataPromiseFactory: PromiseFactory<UserData>;
  cookieService = inject(CookieService);

  constructor(
    utilsService: UtilsService,
    private cacheService: CacheService,
    private storageService: StorageService,
    private router: Router
  ) {
    this.userData = new BehaviorSubject<UserData>({
      administrator: true,
      cityId: 0,
      cityName: '',
      companyId: '',
      companyName: '',
      countryId: 0,
      createdAt: '',
      email: '',
      id: '',
      identification: '',
      lastVisitAt: '',
      name: '',
      servicesAmount: 0,
      usersAmount: 0,
      roleNameList: [],
      roleIdList: [],
      status: 0,
      statusUser: 0,
      terms: true,
      timeEnd: 0,
      userType: 0,
      username: '',
      userPhone: '',
      viewServices: null,
      userPaymentType: null,
      companyPaymentType: null,
    });
    this.userDataValue$ = this.userData.asObservable();
    this.userDataPromiseFactory = utilsService.promiseFactory(
      this.userDataValue$,
      (userData: UserData) => (userData.id ? true : false)
    );
    this.tokenLocal = new BehaviorSubject('');
    this.tokenLocalValue$ = this.tokenLocal.asObservable();
    this.setUserDataInStorage();
    this.setTokenLocalInStorage();
  }

  /**
   * method to set userData value
   *
   * @param {UserData} userData User data
   */
  setUserData(userData: UserData): void {
    if (userData?.userType) {
      userData.userType = parseInt(userData.userType + '', 10);
    }
    const location = localStorage.getItem('location');
    if (userData?.countryId && !location) {
      localStorage.setItem(
        'location',
        environment.countries.find(x => x.id === userData.countryId).geoName
      );
    }
    this.userData.next(userData);
  }

  /**
   * method to get userData
   *
   * @return {Observable<UserData>} userDataValue$
   */
  getUserData(): Observable<UserData> {
    return this.userDataValue$;
  }

  /**
   * convert observable to Promise
   *
   * @returns Promise
   */
  getUserDataAsPromise(): Promise<UserData> {
    return this.userDataPromiseFactory();
  }

  /**
   * method to set current tokenLocal value
   */
  setTokenLocal(tokenLocal: string): void {
    this.tokenLocal.next(tokenLocal);
  }

  /**
   * method to get token local
   *
   * @return {Observable<string>} tokenLocalValue$
   */
  getTokenLocal(): Observable<string> {
    return this.tokenLocalValue$;
  }

  /**
   *
   * @returns string
   */
  getActuallyToken(): string {
    return this.cookieService.get('token-mu') || '';
  }

  setUserDataInStorage(): void {
    this.setUserData(
      this.cookieService.get('mu-data')
        ? JSON.parse(JSON.parse(window.atob(this.cookieService.get('mu-data'))))
        : null
    );
  }

  setTokenLocalInStorage(): void {
    this.setTokenLocal(this.cookieService.get('token-mu'));
  }

  /**
   * method to close session
   */
  async signOut() {
    const path = '/';
    localStorage.setItem('resetFlags', '1');
    this.cookieService.delete('token-mu', path);
    this.cookieService.delete('mu-data', path);
    this.cookieService.delete('token-expires-in', path);
    this.cookieService.delete('token-refresh-token', path);
    this.cookieService.delete('token-token-type', path);
    localStorage.removeItem('mu-data');
    localStorage.removeItem('mu-pl');
    localStorage.removeItem('location');
    localStorage.removeItem('resetFlags');
    localStorage.removeItem('_r_x_a');
    this.storageService.clear(
      StorageClassifier.FILTER,
      StorageClassifier.TEMPORAL,
      StorageClassifier.PERSISTENT_FILTER
    );
    this.cacheService.clearCache();
    this.setTokenLocal('');
    this.setUserData(null);
    this.cookieService.delete('progress_points');
    this.cookieService.deleteAll(path);
    this.router.navigate(['/page/login']);
  }
}
